import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, MoreVertical, Eye, Pencil, Trash2, UploadCloud } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { deleteBlog, getBlogsByAuthor, publishBlog } from '../../../services/Writer/blogService';
import { getCategories } from '../../../services/Writer/blogCategoriesService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

type Blog = {
  blogId: string;
  title: string;
  content: string;
  featuredImage?: string | null;
  tags?: string[] | null;
  referenceLinks?: string[] | null;
  status: 'draft' | 'published';
  blogDate?: string | null;
  authorId: string;
  authorType: 'doctor' | 'blog_writer' | 'writer' | 'admin';
  categoryId: string;
  category?: { name?: string | null } | null;
  createdAt: string;
  updatedAt: string;
};

type Category = {
  categoryId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const WriterBlogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const userId = useMemo(() => localStorage.getItem('userId') || '', []);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmDescription, setConfirmDescription] = useState('');
  const [onConfirm, setOnConfirm] = useState<null | (() => Promise<void>)>(null);

  const filteredBlogs = useMemo(() => {
    if (!selectedCategory) return blogs;
    return blogs.filter((b) => b.categoryId === selectedCategory);
  }, [blogs, selectedCategory]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.categoryId, c.name));
    return map;
  }, [categories]);

  const getBlogCategoryName = (blog: Blog) =>
    categoryNameById.get(blog.categoryId) || blog.category?.name || 'Unknown';

  useEffect(() => {
    const load = async () => {
      if (!userId) {
        toast.error('Missing userId. Please logout and login again.');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [cats, myBlogs] = await Promise.all([getCategories(), getBlogsByAuthor(userId)]);
        setCategories(cats);
        setBlogs(myBlogs);
      } catch (e: any) {
        toast.error(getApiErrorMessage(e, 'Failed to load blogs'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const openConfirm = (opts: {
    title: string;
    description: string;
    action: () => Promise<void>;
  }) => {
    setConfirmTitle(opts.title);
    setConfirmDescription(opts.description);
    setOnConfirm(() => opts.action);
    setConfirmOpen(true);
  };

  const onDelete = (blog: Blog) => {
    openConfirm({
      title: 'Delete blog?',
      description: `“${blog.title}” will be permanently removed. This action cannot be undone.`,
      action: async () => {
        await deleteBlog(blog.blogId);
        setBlogs((prev) => prev.filter((b) => b.blogId !== blog.blogId));
        toast.success('Blog deleted');
      },
    });
  };

  const onPublish = async (blog: Blog) => {
    openConfirm({
      title: 'Publish blog?',
      description: `Publish “${blog.title}”? This will mark the blog as published.`,
      action: async () => {
        await publishBlog(blog.blogId);
        setBlogs((prev) =>
          prev.map((b) => (b.blogId === blog.blogId ? { ...b, status: 'published' } : b))
        );
        toast.success('Blog published');
      },
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">Health Blogs</div>
          <div className="text-gray-600 dark:text-gray-400">Create, edit, and backdate blog posts.</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/writer/blogs/categories')}
            className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            Manage Categories
          </button>
          <button
            onClick={() => navigate('/writer/blogs/create')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition"
          >
            <Plus className="w-4 h-4" />
            New Blog
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        <div className="text-sm text-gray-700 dark:text-gray-300">Category:</div>
        <select
          value={selectedCategory ?? ''}
          onChange={(e) => setSelectedCategory(e.target.value ? e.target.value : null)}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">All</option>
          {categories.map((c) => (
            <option key={c.categoryId} value={c.categoryId}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading...</div>
      ) : filteredBlogs.length === 0 ? (
        <div className="py-16 text-center text-gray-600 dark:text-gray-400">No blogs found.</div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/30 text-gray-700 dark:text-gray-200">
              <tr>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Blog Date</th>
                <th className="text-left p-3">Updated</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((b) => (
                <tr key={b.blogId} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="p-3 font-medium text-gray-900 dark:text-white">{b.title}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{getBlogCategoryName(b)}</td>
                  <td className="p-3">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        b.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
                      }`}
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{formatDate(b.blogDate)}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{formatDate(b.updatedAt)}</td>
                  <td className="p-3">
                    <div className="flex justify-end">
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                            aria-label="Actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            sideOffset={8}
                            align="end"
                            className="min-w-[180px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-1 text-sm"
                          >
                            <DropdownMenu.Item
                              onSelect={() => navigate(`/writer/blogs/view/${b.blogId}`, { state: b })}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              onSelect={() => navigate(`/writer/blogs/edit/${b.blogId}`, { state: b })}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </DropdownMenu.Item>

                            {b.status === 'draft' ? (
                              <DropdownMenu.Item
                                onSelect={(e) => {
                                  e.preventDefault();
                                  onPublish(b);
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                              >
                                <UploadCloud className="w-4 h-4" />
                                Publish
                              </DropdownMenu.Item>
                            ) : null}

                            <DropdownMenu.Separator className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
                            <DropdownMenu.Item
                              onSelect={(e) => {
                                e.preventDefault();
                                onDelete(b);
                              }}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 outline-none"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={2500} />

      <Dialog.Root open={confirmOpen} onOpenChange={(open) => (confirmLoading ? null : setConfirmOpen(open))}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              {confirmTitle}
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {confirmDescription}
            </Dialog.Description>

            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={confirmLoading}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-60"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={confirmLoading || !onConfirm}
                onClick={async () => {
                  if (!onConfirm) return;
                  setConfirmLoading(true);
                  try {
                    await onConfirm();
                    setConfirmOpen(false);
                  } catch (e: any) {
                    toast.error(getApiErrorMessage(e, 'Action failed'));
                  } finally {
                    setConfirmLoading(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition disabled:opacity-60"
              >
                {confirmLoading ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default WriterBlogsPage;

